import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import * as z from "zod";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useState } from "react";

// Zod validation schema
const formSchema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  enrollmentNumber: z.string().min(1, "Enrollment number is required"),
  fatherName: z.string().min(2, "Father's name is required"),
  motherName: z.string().min(2, "Mother's name is required"),
  dateOfBirth: z.date({
    required_error: "Date of birth is required",
  }),
  email: z.string().email("Invalid email address"),
  mobile: z
    .string()
    .length(10, "Mobile number must be exactly 10 digits")
    .regex(/^\d+$/, "Mobile number must contain only digits"),
  address: z.string().min(10, "Full address is required"),
  hasOutstandingFees: z.boolean(),
  reasonForLeaving: z.string().min(10, "Please provide a detailed reason"),
  declaration: z.boolean().refine((val) => val === true, {
    message: "You must agree to the declaration",
  }),
});

export default function TransferCertificateForm() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      hasOutstandingFees: false,
      declaration: false,
    },
  });

  const dateOfBirth = useWatch({
    control: form.control,
    name: "dateOfBirth",
  });


  async function onSubmit(values) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/transfer/create`,
        values
      );
  
      console.log(response.data); // Log the response to inspect its structure
  
      if (response.status === 201) {
        toast.success(
          "Your transfer certificate application has been received."
        );
        form.reset();
        
        if (response.data.newUser) {
          setUsers((prev) => [...prev, response.data.newUser]); // Assuming the response includes the new user
        } else {
          console.error('newUser not found in the response:', response.data);
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error.response?.data?.message || "Error submitting the form."
        );
        console.error("Error submitting form:", error);
      } else {
        console.error("Non-Axios error:", error);
      }
    }
  }
  
  return (
    <div className="min-h-screen p-12 bg-gray-100">
      <div className="container max-w-3xl px-4 mx-auto sm:px-6 lg:px-8">
        <div className="overflow-hidden bg-white border-4 border-teal-500 rounded-lg">
          <div className="p-2 sm:p-4">
            <div className="p-4 mb-6 border-l-4 border-yellow-400 bg-yellow-50">
              <p className="font-medium text-yellow-700">Note:</p>
              <ul className="mt-2 space-y-1 text-sm text-yellow-700 list-disc list-inside">
                <li>
                  Application for TC should be submitted 15 days in advance.
                </li>
                <li>
                  Application will not be processed until all fees and dues are
                  fully cleared.
                </li>
              </ul>
            </div>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col">
                  <label className="font-medium">
                    Student's full name (as per marksheet)
                  </label>
                  <input
                    type="text"
                    className="px-4 py-2 border rounded-lg"
                    placeholder="Enter full name"
                    {...form.register("fullName")}
                  />
                  {form.formState.errors.fullName && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.fullName?.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="font-medium">Enrollment number</label>
                  <input
                    type="text"
                    className="px-4 py-2 border rounded-lg"
                    placeholder="Enter enrollment number"
                    {...form.register("enrollmentNumber")}
                  />
                  {form.formState.errors.enrollmentNumber && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.enrollmentNumber?.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="font-medium">Father's name</label>
                  <input
                    type="text"
                    className="px-4 py-2 border rounded-lg"
                    placeholder="Enter father's name"
                    {...form.register("fatherName")}
                  />
                  {form.formState.errors.fatherName && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.fatherName?.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="font-medium">Mother's name</label>
                  <input
                    type="text"
                    className="px-4 py-2 border rounded-lg"
                    placeholder="Enter mother's name"
                    {...form.register("motherName")}
                  />
                  {form.formState.errors.motherName && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.motherName?.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="font-medium">Date of birth</label>
                  <DatePicker
                    selected={dateOfBirth ? new Date(dateOfBirth) : null}
                    onChange={(date) => form.setValue("dateOfBirth", date)}
                    className="px-4 py-2 border rounded-lg"
                    dateFormat="yyyy-MM-dd"
                    showTimeSelect={false}
                  />
                  {form.formState.errors.dateOfBirth && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.dateOfBirth?.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="font-medium">Email ID</label>
                  <input
                    type="email"
                    className="px-4 py-2 border rounded-lg"
                    placeholder="Enter email address"
                    {...form.register("email")}
                  />
                  {form.formState.errors.email && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.email?.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="font-medium">Mobile number</label>
                  <input
                    type="tel"
                    className="px-4 py-2 border rounded-lg"
                    placeholder="Enter mobile number"
                    {...form.register("mobile")}
                  />
                  {form.formState.errors.mobile && (
                    <p className="text-sm text-red-600">
                      {form.formState.errors.mobile?.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <label className="font-medium">Full address</label>
                <textarea
                  className="px-4 py-2 border rounded-lg"
                  placeholder="Enter your full address"
                  {...form.register("address")}
                />
                {form.formState.errors.address && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.address?.message}
                  </p>
                )}
              </div>

              <div className="flex flex-row items-center">
                <input
                  type="checkbox"
                  {...form.register("hasOutstandingFees")}
                  className="mr-2"
                />
                <label className="font-medium">
                  Whether any fees/dues are outstanding?
                </label>
              </div>

              <div className="flex flex-col">
                <label className="font-medium">
                  Reason for leaving the college
                </label>
                <textarea
                  className="px-4 py-2 border rounded-lg"
                  placeholder="Please provide your reason for leaving"
                  {...form.register("reasonForLeaving")}
                />
                {form.formState.errors.reasonForLeaving && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.reasonForLeaving?.message}
                  </p>
                )}
              </div>

              <div className="flex flex-row items-center">
                <input
                  type="checkbox"
                  {...form.register("declaration")}
                  className="mr-2"
                />
                <label className="font-medium">
                  I hereby declare that all information provided is accurate
                </label>
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  className="w-full py-3 mt-6 font-semibold text-white bg-teal-500 rounded-lg shadow-lg hover:bg-teal-400"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
